import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const SAMPLE = `course_name,week_no,day_no,title,content,youtube_url,question,option1,option2,option3,option4,correct_answer,project_title,project_step
React Training,1,1,Introduction to React,React is a JavaScript library for building user interfaces.,https://youtube.com/watch?v=abc123,What is React?,Library,Framework,Language,Tool,Library,Build a React App,Create a new React project
React Training,1,1,Introduction to React,React is a JavaScript library for building user interfaces.,https://youtube.com/watch?v=abc123,Who created React?,Google,Facebook,Microsoft,Apple,Facebook,Build a React App,Create your first component
React Training,1,2,Components,Components are building blocks of React.,https://youtube.com/watch?v=def456,What are components?,Functions,Classes,Objects,Both A and B,Both A and B,Component Practice,Create a Header component`;

export default function CSVImport() {
  const [file, setFile]   = useState(null);
  const [result, setResult] = useState(null);
  const [logs, setLogs]   = useState([]);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab]     = useState('import');

  const loadLogs = () => api.get('/csv/logs').then(r => setLogs(r.data.data));
  useEffect(() => { loadLogs(); }, []);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    const fd = new FormData();
    fd.append('csv', file);
    try {
      const r = await api.post('/csv/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(r.data.data);
      loadLogs();
    } catch (err) {
      setResult({ error: err.response?.data?.message || 'Upload failed' });
    } finally {
      setUploading(false);
    }
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = 'sample_import.csv'; a.click();
  };

  return (
    <>
      <div className="d-flex gap-2 mb-4">
        {['import','logs'].map(t => (
          <button key={t} className={`btn btn-sm ${tab===t ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setTab(t)}>
            {t === 'import' ? 'Import CSV' : 'Import Logs'}
          </button>
        ))}
      </div>

      {tab === 'import' && (
        <div className="row">
          <div className="col-md-7">
            <div className="content-section">
              <h6><i className="bi bi-file-earmark-arrow-up me-2 text-primary" />Upload CSV File</h6>
              <p className="text-muted small">Upload a CSV file to bulk-import course content. Download the sample template to see the required format.</p>

              <div className="border-2 border-dashed rounded p-4 text-center mb-3" style={{borderStyle:'dashed',borderColor:'#dee2e6',background:'#f8f9fa'}}>
                <i className="bi bi-cloud-upload fs-2 text-muted mb-2 d-block" />
                <label className="btn btn-outline-primary btn-sm mb-2">
                  Choose CSV File
                  <input type="file" accept=".csv" className="d-none" onChange={e => setFile(e.target.files[0])} />
                </label>
                {file && <p className="mb-0 small text-success"><i className="bi bi-check-circle me-1" />{file.name}</p>}
              </div>

              {result && (
                <div className={`alert ${result.error ? 'alert-danger' : 'alert-success'}`}>
                  {result.error ? (
                    <>{result.error}</>
                  ) : (
                    <>
                      <strong><i className="bi bi-check-circle me-1" />Import Complete</strong>
                      <div className="mt-1 small">
                        <span className="text-success me-3">✓ {result.imported} rows imported</span>
                        {result.failed > 0 && <span className="text-danger">✗ {result.failed} rows failed</span>}
                      </div>
                      {result.errors?.length > 0 && (
                        <details className="mt-2">
                          <summary className="small cursor-pointer">View errors</summary>
                          <ul className="small mt-1 mb-0">
                            {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                          </ul>
                        </details>
                      )}
                    </>
                  )}
                </div>
              )}

              <div className="d-flex gap-2">
                <button className="btn btn-primary" onClick={upload} disabled={!file || uploading}>
                  {uploading ? <><span className="spinner-border spinner-border-sm me-2" />Importing...</> : <><i className="bi bi-upload me-1" />Import</>}
                </button>
                <button className="btn btn-outline-secondary" onClick={downloadSample}>
                  <i className="bi bi-download me-1" />Sample CSV
                </button>
              </div>
            </div>
          </div>
          <div className="col-md-5">
            <div className="content-section">
              <h6><i className="bi bi-info-circle me-2 text-info" />CSV Format</h6>
              <p className="small text-muted mb-2">Required columns:</p>
              <ul className="small text-muted ps-3">
                <li><strong>course_name</strong> — Course title</li>
                <li><strong>week_no</strong> — Week number (1–n)</li>
                <li><strong>day_no</strong> — Day number (1–5)</li>
                <li><strong>title</strong> — Day title</li>
                <li><strong>content</strong> — Text content</li>
                <li><strong>youtube_url</strong> — YouTube link (optional)</li>
                <li><strong>question</strong> — Quiz question (optional)</li>
                <li><strong>option1–4</strong> — Answer options</li>
                <li><strong>correct_answer</strong> — Correct option text</li>
                <li><strong>project_title</strong> — Project name (optional)</li>
                <li><strong>project_step</strong> — Step text (optional)</li>
              </ul>
              <p className="small text-muted">Multiple questions per day: repeat the row with the same course/week/day and different question.</p>
            </div>
          </div>
        </div>
      )}

      {tab === 'logs' && (
        <div className="table-card">
          <table className="table table-hover align-middle mb-0">
            <thead><tr><th>File</th><th>Imported</th><th>Failed</th><th>By</th><th>Date</th></tr></thead>
            <tbody>
              {logs.length === 0 ? (
                <tr><td colSpan={5} className="text-center text-muted py-3">No import logs yet</td></tr>
              ) : logs.map(l => (
                <tr key={l.id}>
                  <td className="small">{l.file_name}</td>
                  <td><span className="badge bg-success">{l.imported_rows}</span></td>
                  <td>{l.failed_rows > 0 ? <span className="badge bg-danger">{l.failed_rows}</span> : <span className="text-muted small">0</span>}</td>
                  <td className="small text-muted">{l.imported_by_name}</td>
                  <td className="small text-muted">{new Date(l.created_at).toLocaleString('en-IN')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
